// v1.5.0 · Kit de las pantallas animadas de proyectos: reloj de datos en vivo, contador animado, piezas de panel
//           y el carrusel de 5 pantallas por proyecto (avance automático, pausa al pasar el mouse o fuera de vista).
import React, { useEffect, useRef, useState } from 'react';

export function useReduced() {
  const [r, setR] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const m = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!m) return undefined;
    const on = () => setR(m.matches);
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, []);
  return r;
}

/** Contador que avanza cada `ms` mientras la pantalla está montada (los datos «en vivo» se derivan de él). */
export function useTick(ms = 1600) {
  const reduced = useReduced();
  const [t, setT] = useState(0);
  useEffect(() => {
    if (reduced) return undefined;
    const id = setInterval(() => setT((x) => x + 1), ms);
    return () => clearInterval(id);
  }, [ms, reduced]);
  return t;
}

/** Formato numérico de Ecuador: 12.483 y 96,4 */
export function fmt(n, dec = 0) {
  const neg = n < 0;
  const [i, d] = Math.abs(n).toFixed(dec).split('.');
  const s = i.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (neg ? '−' : '') + s + (d ? `,${d}` : '');
}

/** Número que sube animado hasta `value` (y vuelve a animar cuando cambia). */
export function Count({ value, dec = 0, dur = 1100, prefix = '', suffix = '' }) {
  const reduced = useReduced();
  const [v, setV] = useState(reduced ? value : 0);
  const from = useRef(reduced ? value : 0);
  useEffect(() => {
    if (reduced) { setV(value); return undefined; }
    let raf; const start = performance.now(); const a = from.current;
    const step = (now) => {
      const k = Math.min(1, (now - start) / dur);
      const e = 1 - Math.pow(1 - k, 3);
      const x = a + (value - a) * e;
      setV(x);
      if (k < 1) raf = requestAnimationFrame(step); else from.current = value;
    };
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); from.current = value; };
  }, [value, dur, reduced]);
  return <span className="num">{prefix}{fmt(v, dec)}{suffix}</span>;
}

/** Ruido determinista (sin Math.random): valores «en vivo» estables entre renders */
export const wave = (t, k = 1, amp = 1) => Math.sin(t * 0.9 * k + k * 1.7) * amp;

export function Head({ title, sub, right }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="truncate text-[14px] font-bold">{title}</div>
        {sub && <div className="dim truncate text-[11.5px]">{sub}</div>}
      </div>
      {right}
    </div>
  );
}

export function Live({ label = 'En vivo' }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[rgba(79,191,106,.14)] px-2 py-0.5 text-[10.5px] font-semibold text-[#8BE07F]">
      <span className="blink h-1.5 w-1.5 rounded-full bg-[#6FD66A]" aria-hidden /> {label}
    </span>
  );
}

export function Kpi({ label, children, tone }) {
  const c = tone === 'ok' ? 'text-[#8BE07F]' : tone === 'warn' ? 'text-[#F2C266]' : tone === 'bad' ? 'text-[#FF8F82]' : '';
  return (
    <div className="dash-panel min-w-0 px-3 py-2.5">
      <div className={`text-[20px] font-extrabold leading-none ${c}`}>{children}</div>
      <div className="dim mt-1.5 truncate text-[11px]">{label}</div>
    </div>
  );
}

/** Estado con ícono + texto (nunca solo color) */
export function Status({ tone = 'ok', children }) {
  const map = {
    ok: ['bg-[rgba(79,191,106,.16)] text-[#8BE07F]', '●'],
    warn: ['bg-[rgba(233,162,59,.16)] text-[#F2C266]', '▲'],
    bad: ['bg-[rgba(230,103,103,.18)] text-[#FF8F82]', '■'],
    info: ['bg-[rgba(46,155,214,.18)] text-[#8CCBEA]', '◆'],
  }[tone];
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${map[0]}`}>
      <span aria-hidden className="text-[8px]">{map[1]}</span>{children}
    </span>
  );
}

/** Carrusel de pantallas: chips con barra de avance, autoavance, pausa al pasar el mouse o fuera de vista. */
export function Screens({ screens, dur = 6500, label }) {
  const reduced = useReduced();
  const [i, setI] = useState(0);
  const [hover, setHover] = useState(false);
  const [inView, setInView] = useState(false);
  const box = useRef(null);
  const paused = hover || !inView || reduced;

  useEffect(() => {
    const el = box.current;
    if (!el || !('IntersectionObserver' in window)) { setInView(true); return undefined; }
    const o = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.3 });
    o.observe(el);
    return () => o.disconnect();
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const id = setTimeout(() => setI((x) => (x + 1) % screens.length), dur);
    return () => clearTimeout(id);
  }, [i, paused, dur, screens.length]);

  const C = screens[i].C;
  return (
    <div ref={box} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
         onFocus={() => setHover(true)} onBlur={() => setHover(false)}>
      <div className="dash-chips mb-3 flex gap-1.5 overflow-x-auto pb-1" data-paused={paused} role="tablist" aria-label={label}>
        {screens.map((s, k) => (
          <button key={s.t} role="tab" aria-selected={k === i} onClick={() => setI(k)}
                  className={`relative shrink-0 overflow-hidden rounded-[7px] border px-3 py-1.5 text-[12.5px] font-semibold transition-colors duration-150 ${
                    k === i ? 'border-[rgba(87,211,255,.55)] bg-[rgba(87,211,255,.1)] text-white' : 'border-white/10 text-white/60 hover:border-white/30 hover:text-white/85'
                  }`}>
            <span className="num mr-1.5 text-white/40">{k + 1}</span>{s.t}
            {k === i && !reduced && (
              <span key={`${i}-${paused}`} aria-hidden className="chip-progress absolute inset-x-0 bottom-0 h-[2px] bg-[#57D3FF]"
                    style={{ '--dur': `${dur}ms`, animationPlayState: paused ? 'paused' : 'running' }} />
            )}
          </button>
        ))}
      </div>
      <div className="dash relative h-[540px] sm:h-[400px]">
        <div key={i} className="dash-screen absolute inset-0" role="tabpanel" aria-label={screens[i].t}>
          <C />
        </div>
      </div>
    </div>
  );
}
